import { useCallback, useEffect, useState } from "react";
import packageJson from "../../package.json";

/** Fired on window when a user marks the current version line as seen, so every banner and badge updates */
export const SEEN_VERSION_EVENT = "customer-seen-version";

/** The version line of a version: "1.20.3" → "1.20". Hotfixes share the line, so they never count as new */
export const versionLineOf = (version: string): string => version.split(".").slice(0, 2).join(".");

/** Compares two version lines numerically ("1.100" is newer than "1.99") */
const compareLines = (a: string, b: string): number => {
  const [majorA, minorA] = a.split(".").map(Number);
  const [majorB, minorB] = b.split(".").map(Number);

  return majorA !== majorB ? majorA - majorB : minorA - minorB;
};

const storageKey = (userKey: string) => `@Customer:seenVersionLine:${userKey}`;

/** Reads the line this user last marked as seen in this browser; null when never, or when storage is blocked */
const readSeenLine = (userKey: string): string | null => {
  try {
    return localStorage.getItem(storageKey(userKey));
  } catch (err) {
    return null;
  }
};

interface SeenVersion {
  /** The running version, as in package.json ("1.20.0") */
  version: string;
  /** Its version line ("1.20") */
  line: string;
  /** True while this user has not seen the Novidades of this version line */
  unseen: boolean;
  /** Marks the current version line as seen for this user, in this browser */
  markSeen: () => void;
}

/**
 * Tracks whether a user has seen the Novidades of the running version line. The mark is kept per user in
 * the browser, so a new line (1.20, 1.21…) shows the banner and the "Novo" badge once; hotfixes do not.
 * @param userKey The signed-in user (the portal session carries the e-mail, not an id); without one nothing is unseen
 * @param version The running version, package.json by default
 */
export function useSeenVersion(userKey?: string, version: string = packageJson.version): SeenVersion {
  const line = versionLineOf(version);
  const [seen, setSeen] = useState<string | null>(() => (userKey ? readSeenLine(userKey) : null));

  useEffect(() => {
    if (!userKey) return undefined;

    setSeen(readSeenLine(userKey));

    // keeps the line already marked in this page when storage is blocked
    const sync = () => setSeen((current) => readSeenLine(userKey) ?? current);

    window.addEventListener(SEEN_VERSION_EVENT, sync);
    window.addEventListener("storage", sync);

    return () => {
      window.removeEventListener(SEEN_VERSION_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [userKey]);

  const markSeen = useCallback(() => {
    if (!userKey) return;

    try {
      localStorage.setItem(storageKey(userKey), line);
    } catch (err) {
      // storage blocked: the mark lasts for this page only
    }

    setSeen(line);
    window.dispatchEvent(new CustomEvent(SEEN_VERSION_EVENT));
  }, [userKey, line]);

  return { version, line, unseen: !!userKey && (!seen || compareLines(line, seen) > 0), markSeen };
}
